<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div
    class="orangehrm-buzz-notifications orangehrm-buzz-notifications--mobile"
  >
    <div class="orangehrm-buzz-notifications-header">
      <oxd-text type="card-title" class="orangehrm-buzz-notifications-title">
        {{ $t('buzz.buzz_notifications') }}
      </oxd-text>
      <oxd-button
        v-if="notifications.length > 0 && unreadCount > 0"
        display-type="ghost"
        size="small"
        :label="$t('buzz.mark_all_as_read')"
        @click="onMarkAllRead"
      />
    </div>

    <oxd-loading-spinner
      v-if="isLoading"
      class="orangehrm-buzz-notifications-loader"
    />

    <div v-else class="orangehrm-buzz-notifications-list">
      <button
        v-for="item in notifications"
        :key="item.id"
        type="button"
        class="orangehrm-buzz-notifications-item"
        :class="{'is-unread': !item.isRead}"
        @click="onNotificationClick(item)"
      >
        <profile-image
          :employee="item.performer"
          class="orangehrm-buzz-notifications-avatar"
        />
        <div class="orangehrm-buzz-notifications-body">
          <oxd-text tag="p" class="orangehrm-buzz-notifications-message">
            {{ notificationMessage(item) }}
          </oxd-text>
          <oxd-text
            v-if="item.contentExcerpt"
            tag="p"
            class="orangehrm-buzz-notifications-excerpt"
          >
            {{ item.contentExcerpt }}
          </oxd-text>
          <oxd-text tag="p" class="orangehrm-buzz-notifications-time">
            {{ formatNotificationTime(item) }}
          </oxd-text>
        </div>
      </button>

      <div v-if="isEmpty" class="orangehrm-buzz-notifications-empty">
        <oxd-icon name="bell" class="orangehrm-buzz-notifications-empty-icon" />
        <oxd-text tag="p">{{ $t('buzz.no_notifications') }}</oxd-text>
      </div>

      <oxd-button
        v-if="hasMore && !isEmpty"
        display-type="ghost"
        class="orangehrm-buzz-notifications-view-more"
        :label="$t('general.show_more')"
        :loading="isLoadingMore"
        @click="loadMoreNotifications"
      />
    </div>
  </div>
</template>

<script>
import {onMounted} from 'vue';
import {OxdIcon, OxdButton} from '@ohrm/oxd';
import useBuzzNotifications from '@/orangehrmBuzzPlugin/util/composable/useBuzzNotifications';
import ProfileImage from '@/orangehrmBuzzPlugin/components/ProfileImage';

export default {
  name: 'BuzzNotifications',

  components: {
    'oxd-icon': OxdIcon,
    'oxd-button': OxdButton,
    'profile-image': ProfileImage,
  },

  emits: ['unread-count', 'go-to-feed'],

  setup(_, {emit}) {
    const {
      isLoading,
      isLoadingMore,
      notifications,
      unreadCount,
      isEmpty,
      hasMore,
      fetchNotifications,
      loadMoreNotifications,
      notificationMessage,
      formatNotificationTime,
      markAsRead,
      onMarkAllRead,
    } = useBuzzNotifications({
      onUnreadCount: (count) => emit('unread-count', count),
    });

    const onNotificationClick = (item) => {
      markAsRead(item).finally(() => {
        emit('go-to-feed', item);
      });
    };

    onMounted(() => fetchNotifications({reset: true}));

    return {
      isLoading,
      isLoadingMore,
      notifications,
      unreadCount,
      isEmpty,
      hasMore,
      notificationMessage,
      formatNotificationTime,
      onNotificationClick,
      onMarkAllRead,
      loadMoreNotifications,
      refresh: () => fetchNotifications({reset: true}),
    };
  },
};
</script>

<style src="./buzz-notifications.scss" lang="scss" scoped></style>
